import PrimaryButton from "components/buttons/primary-button";
import Typography from "components/typography";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Colors } from "utils/colors";
import TickIcon from "./tickIcon";
import Slider from "components/inputs/slider";

const LabelCard = ({
  isMonthly,
  currentPlan,
  loading
}: {
  isMonthly: boolean;
  currentPlan?: boolean;
  loading?: boolean;
}) => {
  const navigate = useNavigate();
  const [artists, setArtists] = useState<keyof typeof prices>(3);

  const Confirm = () => {
    navigate("/pricing/summary", {
      state: { isMonthly, ActivePackage: "label" }
    });
  };

  const rangeSteps = [3, 5, 10, 20, 50, 100];
  const prices: { [key: number]: number } = {
    3: 8,
    5: 12,
    10: 24,
    20: 48,
    50: 64,
    100: 128
  };

  return (
    <Container>
      <Header>
        <HeaderText>
          <Typography weight="semibold" size="xl" mb="0.8">
            Label
          </Typography>
          <Typography color={Colors.grey500} size="s">
            Best For Music Labels and Managers
          </Typography>
        </HeaderText>
        <Icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 22 22"
            fill="none"
          >
            <path
              d="M3.58924 7.71432H18.9108M3.12591 8.16402L10.7775 18.0884C10.8331 18.1609 10.9046 18.2196 10.9866 18.26C11.0685 18.3004 11.1586 18.3215 11.25 18.3215C11.3414 18.3215 11.4315 18.3004 11.5134 18.26C11.5954 18.2196 11.6669 18.1609 11.7225 18.0884L19.3741 8.16402C19.4493 8.06607 19.493 7.94754 19.4992 7.82418C19.5055 7.70083 19.474 7.5785 19.4091 7.47344L16.8136 3.28396C16.7602 3.19726 16.6855 3.12566 16.5966 3.07596C16.5077 3.02626 16.4076 3.00011 16.3057 3H6.19426C6.09242 3.00011 5.9923 3.02626 5.90341 3.07596C5.81451 3.12566 5.7398 3.19726 5.68637 3.28396L3.09092 7.47344C3.02597 7.5785 2.99453 7.70083 3.00078 7.82418C3.00703 7.94754 3.05067 8.06607 3.12591 8.16402Z"
              stroke="#F5F5F5"
              stroke-width="1.17858"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M16.5535 3.58929L14.7856 7.71432M14.7856 7.71432L11.2499 3L7.71416 7.71432M14.7856 7.71432L11.2499 17.7322L7.71416 7.71432M5.94629 3.58929L7.71416 7.71432"
              stroke="#F5F5F5"
              stroke-width="1.17858"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </Icon>
      </Header>
      <PriceDiv>
        <Typography size="4.8rem" weight="semibold" mr="0.8">
          ${prices[artists]}
        </Typography>
        <Typography lh="4" color={Colors.grey500}>
          per month
        </Typography>
      </PriceDiv>
      <Typography color={Colors.yellow400} weight="medium" mb="3.2">
        Artists: {artists}
      </Typography>
      <SliderContainer>
        <Slider
          min={0}
          max={rangeSteps.length - 1}
          handleSlide={(value) => {
            setArtists(rangeSteps[value]);
          }}
          value={rangeSteps.indexOf(artists)}
        />
      </SliderContainer>
      <CheckItem>
        <TickIcon />
        <Typography
          ml="0.4"
          weight="medium"
          decoration="underline"
          color={Colors.yellow400}
        >
          All Independent and Pro Features
        </Typography>
      </CheckItem>{" "}
      <CheckItem>
        <TickIcon />
        <Typography color={Colors.grey300} ml="0.4" weight="medium">
          1TB instant file sharing
        </Typography>
      </CheckItem>{" "}
      <CheckItem>
        <TickIcon />
        <Typography color={Colors.grey300} ml="0.4" weight="medium">
          Monitor your music on Spotify and Apple Music
        </Typography>
      </CheckItem>{" "}
      <Button>
        <PrimaryButton
          text={currentPlan ? "Current Plan" : `Select Label (${artists})`}
          inset
          variant="white"
          onClick={Confirm}
          disabled={currentPlan}
          loading={loading}
        />
      </Button>
    </Container>
  );
};

export default LabelCard;

const Container = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 0.8rem;
  border: 1px solid #4e4c44;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  min-height: 54rem;
  background: linear-gradient(
    108deg,
    rgba(30, 30, 30, 0.8) 0.85%,
    rgba(19, 19, 19, 0.8) 100%
  );
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2.4rem;
  @media only screen and (max-width: 769px) {
    margin-bottom: 2.4rem;
  }
`;

const HeaderText = styled.div``;

const Icon = styled.div`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  border-top: 1px solid #a9a28c;
  background: #0d0d0d;
  box-shadow: 0px 4px 4px 0px rgba(50, 50, 50, 0.5) inset;
  padding: 0.6rem;
`;

const PriceDiv = styled.div`
  display: flex;
  align-items: end;
  margin-bottom: 2.4rem;
`;

const SliderContainer = styled.div`
  margin-bottom: 0.8rem;
`;
const CheckItem = styled.div`
  margin-top: 2.4rem;
  display: flex;
  align-items: center;
`;

const Button = styled.div`
  margin-top: auto;
`;
